import { create } from 'zustand';
import { ROOMS } from '../rooms';
import { api } from '../services/api';
import { useWorldStore } from '../state/worldStore';
import { siteKey } from '../services/roomSpace';
import { seatActive } from '../services/worldSocket';
import COVER_LINES from '../data/hypeu_cover_lines.json';

// HYPE-U — the university's state (2026-11-01). Khari: "When they step into the
// elevator, the floor buttons are labeled after the course title. When the
// elevator makes it to the floor, open the door to a small classroom with the
// artist/instructor standing in front of a whiteboard."
//
// THE COURSES are the learn page's (API GET /api/learn — the website's /learn,
// same hosts, same lessons in their words, their recorded readings), loaded
// once and kept.
//
// ONE CLASSROOM PER COURSE, ONE MODEL: each course gets a room id
// (`hypeu-class-<slug>`) registered on ROOMS.hypeu_class with its `course`
// added — the instructor, the board and the lesson are the course's, the room is
// the same (venueBase: a tuned classroom tunes them all). Slugs are stable (API
// config/learn/courses: "retitle, never re-slug"), so the ids are too.
//
// THE RIDE (rideTo): the doors close, the car moves (a beat, the screen dims),
// you step out on the other floor in its car, and its doors open. `ride` is what
// Elevator (the doors, in the canvas) and ElevatorPanel (the buttons) read.

export const CLASS_PREFIX = 'hypeu-class-';
export const classroomId = (slug) => `${CLASS_PREFIX}${slug}`;

export const DOOR_MS = 1100;   // the doors closing (and, on arrival, opening)
export const MOVE_MS = 1500;   // the car moving between floors

export const useHypeU = create(() => ({
  courses: null,     // the learn page's courses, once loaded ([] = none / failed)
  inCar: false,      // standing in this room's elevator car (Elevator)
  arrivedIn: false,  // …because a ride just set you down there (the floors wait behind a button until you step out)
  aboard: false,     // the elevator has you: the camera behind you, walking stood down (ElevatorDirector)
  ride: null,        // { to, phase: 'closing' | 'moving' | 'opening', at }
  lobby: null,       // the lobby you rode up from (a city instance's room id)
  talking: false,    // the instructor's voice is playing (CourseRunner → Classroom)
  // THE CLASS, as the board and its controls draw it (CourseRunner writes it):
  //   lesson { page, total, state, note, pageTitle, intro, body[], host }
  //   qa     { open, q, a, by, thinking, note, cta } — a question to the instructor
  //          (`by`: whose recorded answer it is, when the substitute is in)
  // `state`: idle | greeting | loading | playing | paused | between | done | error | unwritten
  //   (greeting: the substitute's recorded hello before the lesson, `line` its words)
  lesson: null,
  qa: null,
  liveVoice: null,   // the catalogue's { limit, used: { [course]: seconds } } (members' live voice per course)
  // WHO IS IN THE CLASSROOM (CourseRunner asks API GET /api/learn/class/:course):
  //   { slug, cover: null | { by: { avatar_id, name }, kind, until, why } }
  // `cover` = the tutor is out (performing or asleep) and the substitute stands in.
  cover: null,
}));

/**
 * THE SUBSTITUTE (2026-11-04). Khari: "Lyra sits in when the tutor is out, but
 * she does not teach the class … plays a slideshow for the students. This way,
 * we can use the existing recordings by the original tutors. Lyra can step in
 * when a student has a question." She stands at the teacher's mark ("if it is
 * easier to have her stand in the same position as the other teachers, that is
 * fine"); the readings and the recorded answers stay the tutor's; a question
 * asked live goes to her (API LearnService.coverFor).
 * Who is in this classroom for `course` (city/hypeu `course`), given the store's
 * `cover`: `known` is false until the API has said (nobody stands there yet).
 */
export function teacherOf(course, cover) {
  const hostName = course?.hostName || 'Your teacher';
  const hostFirst = hostName.split(' ')[0];
  const known = !!course && cover?.slug === course.slug;
  const c = known ? cover.cover : null;
  const name = c?.by?.name || hostName;
  return {
    known,
    id: c?.by?.avatar_id ?? course?.hostId ?? null,
    name, first: name.split(' ')[0],
    sub: !!c, why: c?.why || null, kind: c?.kind || null,
    hostName, hostFirst,
  };
}

/**
 * HER HELLO (2026-11-04, Khari: "agreed with lyra's recorded clips"): one line
 * per tutor for each reason they are out, in her own words and voice (API
 * scripts/write_cover_lines.js, then record_room_greetings.js → public/audio/
 * hypeu). Played once when you sit down while she is in (CourseRunner).
 * Performing covers a set, a freestyle and a City Story alike.
 * @returns {null | { id, text, url }}
 */
export function coverLine(course, teacher) {
  if (!course?.hostId || !teacher?.sub) return null;
  const id = `${course.hostId}_${teacher.kind === 'asleep' ? 'asleep' : 'performing'}`;
  const line = (COVER_LINES.lines || []).find((l) => l.id === id);
  return line ? { id, text: line.text, url: `${COVER_LINES.audio.replace('{id}', id)}?v=${COVER_LINES.v}` } : null;
}

/**
 * The class's buttons, as CourseRunner provides them (it owns the audio and the
 * asking): the board's controls (Classroom, in the canvas) and the phone sheet
 * (CourseRunner) both press these. { back, toggle, next, openAsk, closeAsk, send(text), chip(i) }.
 */
export const classCtl = {};

let lastKeep = 0;
/**
 * KEEP MY CHAIR (rooms.js `seatKeepAlive`): you are busy in it — the class is
 * running, you asked something, you pressed a control — so its two minutes
 * start again from now (worldSocket seatActive; the server caps it). At most
 * every 20s unless `force` (a question asked is always worth saying).
 * The new `until` re-arms SceneStaging's stand-up timer.
 */
export function keepSeat(force = false) {
  const ws = useWorldStore.getState();
  const s = ws.seated;
  if (!s?.zone || !s.until || s.phase === 'standing' || !ROOMS[s.zone]?.seatKeepAlive) return;
  const now = Date.now();
  if (!force && now - lastKeep < 20000) return;
  lastKeep = now;
  seatActive(s.zone, s.target).then((r) => {
    if (!r?.ok || !r.until) return;
    useWorldStore.setState((x) => (x.seated && x.seated.target === s.target && x.seated.phase !== 'standing' && r.until > (x.seated.until || 0)
      ? { seated: { ...x.seated, until: r.until } } : x));
  });
}

/**
 * The elevator of the room you are in, as its own component publishes it
 * (components/hypeu/Elevator): which room, its fit (`k`) and the group its
 * rooms.js numbers are in. ElevatorDirector frames you with it — trusted only
 * when `roomId` is the room you are in (mid-ride, the old one is going).
 */
export const elevatorBridge = { roomId: null, k: 1, space: null };

/** Step out of the car without riding: the camera is yours again until you walk back in. */
export function stepOut() { useHypeU.setState({ arrivedIn: true }); }

/** The course a room is the classroom of, or null. */
export const courseOf = (roomId) => ROOMS[roomId]?.course || null;

/**
 * A CLASSROOM IS A FLOOR OF THE BUILDING (2026-11-01). Khari: "there are windows
 * in the classroom, we should place the city view outside, but from an elevated
 * view to simulate being on a higher floor." With a Hype-U standing in the city
 * (the 'university' type is sited), every classroom stands on that building's
 * site (services/roomSpace siteKey) at its own floor's height: the lobby is
 * 5.25m to its roof, then 4m a floor, in the order the elevator lists them —
 * the windows look out over the real city from up there, and the painted view
 * behind them (`window_views`) comes down. With no Hype-U in the city yet, the
 * classroom is a room of its own and the painting stays.
 */
const LOBBY_TOP = 2.5;      // world units (1u ≈ 2.2m): the first classroom's floor
const FLOOR_STEP = 1.82;    // floor to floor (4m)

/** Register a course's classroom (idempotent); returns its room id. */
export function ensureClassroom(course) {
  const id = classroomId(course.slug);
  if (!ROOMS[id] && ROOMS.hypeu_class) {
    const room = {
      ...ROOMS.hypeu_class,
      name: `${course.title} · Hype-U`,
      venueBase: 'hypeu_class',
      sitedHide: ['window_views'],
      course: {
        slug: course.slug,
        title: course.title,
        hostId: Number(course.host?.avatar_id) || null,
        hostName: course.host?.name || '',
        lesson: course.lessons?.[0]?.slug || null,
      },
    };
    // Asked each time, not fixed at registration: the city's venues load on their own clock.
    Object.defineProperty(room, 'placement', { enumerable: true, get: () => (siteKey(id) !== id ? 'sited' : undefined) });
    Object.defineProperty(room, 'floorY', {
      enumerable: true,
      get: () => {
        const i = (useHypeU.getState().courses || []).findIndex((c) => c.slug === course.slug);
        return LOBBY_TOP + Math.max(0, i) * FLOOR_STEP;
      },
    });
    ROOMS[id] = room;
  }
  return id;
}

let loading = null;
/** The courses (cached for the session); registers every classroom on the way. */
export function loadCourses() {
  if (useHypeU.getState().courses) return Promise.resolve(useHypeU.getState().courses);
  if (!loading) {
    loading = api.learn()
      .then((d) => {
        const list = (d?.courses || []).filter((c) => c?.slug && c?.title);
        list.forEach(ensureClassroom);
        useHypeU.setState({ courses: list, liveVoice: d?.liveVoice || null });
        return list;
      })
      .catch((e) => {
        console.warn('[hypeu] the courses did not load:', e?.message || e);
        useHypeU.setState({ courses: [] });
        loading = null;   // a later visit may try again
        return [];
      });
  }
  return loading;
}

/** The lobby to ride down to: the one you came up from, else any Hype-U lobby there is. */
export function lobbyId() {
  const remembered = useHypeU.getState().lobby;
  if (remembered && ROOMS[remembered]) return remembered;
  const instance = Object.entries(ROOMS).find(([id, r]) => r?.hypeu === 'lobby' && id !== 'hypeu');
  return instance ? instance[0] : 'hypeu';
}

/**
 * Ride the elevator to `roomId` (a classroom, or a lobby). Doors close, the car
 * moves, and you arrive in the other floor's car (its `elevator.arrive`) as its
 * doors open. One ride at a time.
 */
export function rideTo(roomId) {
  const hu = useHypeU.getState();
  const ws = useWorldStore.getState();
  if (hu.ride || !ROOMS[roomId] || roomId === ws.interior) return;
  if (ROOMS[ws.interior]?.hypeu === 'lobby') useHypeU.setState({ lobby: ws.interior });
  useHypeU.setState({ ride: { to: roomId, phase: 'closing', at: performance.now() } });
  setTimeout(() => useHypeU.setState((s) => (s.ride ? { ride: { ...s.ride, phase: 'moving', at: performance.now() } } : {})), DOOR_MS);
  setTimeout(() => {
    const w = useWorldStore.getState();
    w.enterInterior(roomId);
    // enterInterior clears any override, so the arrival is set after it (Portals' rule).
    const a = ROOMS[roomId]?.elevator?.arrive;
    if (a) w.setSpawnOverride({ pos: a.pos, look: a.look });
    useHypeU.setState({ ride: { to: roomId, phase: 'opening', at: performance.now() }, inCar: true, arrivedIn: true, talking: false, lesson: null, qa: null });
  }, DOOR_MS + MOVE_MS);
  setTimeout(() => useHypeU.setState({ ride: null }), DOOR_MS * 2 + MOVE_MS + 400);
}
